import {
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    Step,
    StepLabel,
    Stepper,
    Typography,
} from "@mui/material";
import type { GridRenderCellParams, GridRowParams } from "@mui/x-data-grid-pro";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";
import AcxButton from "components/UI/AcxButton";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import {
    AgentSkillLibraryItem,
    AgentSkillScope,
    AgentSkillSyncOrganization,
    AgentSkillSyncPreviewItem,
    AgentSkillSyncPreviewStatus,
    AgentSkillSyncResponse,
    AgentSkillSyncResultStatus,
} from "components/AgentSkills/Models/AgentSkill";
import AgentSkillsStore from "components/AgentSkills/Stores/AgentSkillsStore";
import { useStore } from "utils/useStore";

interface AgentSkillLibrarySyncDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

type ScopeFilter = "All" | AgentSkillScope;

interface PreviewRow extends AgentSkillSyncPreviewItem {
    id: string;
    organizationId: string;
    organizationName: string;
    scopes: AgentSkillScope[];
}

interface ResultRow {
    id: string;
    organizationName: string;
    skillName: string;
    status: string;
}

const scopeLabels: Record<AgentSkillScope, string> = {
    KboChat: "KBo Chat",
    KboFlow: "KBo Flow",
};

const scopeOptions: ScopeFilter[] = ["All", "KboChat", "KboFlow"];

const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString() : "-";

const renderScopes = (scopes: AgentSkillScope[]) =>
    scopes.length ? (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {scopes.map((scope) => (
                <Chip
                    key={scope}
                    label={scopeLabels[scope]}
                    size="small"
                    variant="outlined"
                />
            ))}
        </Stack>
    ) : (
        <Chip
            label="No scopes"
            size="small"
            color="warning"
            variant="outlined"
        />
    );

const previewStatusLabel: Record<AgentSkillSyncPreviewStatus, string> = {
    Missing: "Missing",
    CurrentLibraryVersion: "Current version",
    DifferentVersion: "Out of date",
    TenantOwned: "Tenant owned",
    NameConflict: "Name conflict",
    DuplicateActiveVersions: "Duplicate active versions",
    HistoricalIdConflict: "Historical ID conflict",
};

const isActionable = (status: AgentSkillSyncPreviewStatus) =>
    status === "Missing" || status === "DifferentVersion";

const defaultAction = (status: AgentSkillSyncPreviewStatus) =>
    status === "Missing" || status === "DifferentVersion";

const syncResultLabels: Record<AgentSkillSyncResultStatus, string> = {
    Missing: "Skipped: missing",
    CurrentLibraryVersion: "Already up to date",
    DifferentVersion: "Update available",
    DifferentLibraryVersion: "Skipped: update not selected",
    TenantOwned: "Skipped: tenant owned",
    NameConflict: "Skipped: name conflict",
    DuplicateActiveVersions: "Skipped: duplicate active versions",
    HistoricalIdConflict: "Skipped: version ID conflict",
    Legacy: "Skipped: resave required",
    Created: "Created",
    Updated: "Updated",
    Restored: "Restored",
};

const createSelectionGridStore = (gridId: string, rowTerm: string) => {
    const gridStore = new AcxDataGridStore(gridId, "Administrative");
    gridStore.checkboxSelection = true;
    gridStore.disableRowSelectionOnClick = true;
    gridStore.pagination = true;
    gridStore.rowTerm = rowTerm;
    gridStore.heightOverride = "clamp(280px, 42vh, 460px)";
    gridStore.onPaginationModelChange = (model) => {
        gridStore.paginationModel = model;
    };
    return gridStore;
};

const AgentSkillLibrarySyncDialog: React.FC<AgentSkillLibrarySyncDialogProps> =
    observer(({ isOpen, onClose }) => {
        const store = useStore(AgentSkillsStore);
        const [step, setStep] = useState(0);
        const [organizations, setOrganizations] = useState<
            AgentSkillSyncOrganization[]
        >([]);
        const [scopeFilter, setScopeFilter] = useState<ScopeFilter>("All");
        const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
        const [selectedActions, setSelectedActions] = useState<
            Record<string, boolean>
        >({});
        const [syncResult, setSyncResult] = useState<
            AgentSkillSyncResponse | undefined
        >();
        const [confirmSync, setConfirmSync] = useState(false);
        const organizationGridStore = useMemo(
            () =>
                createSelectionGridStore(
                    "AgentSkillLibrarySyncOrganizations",
                    "organizations",
                ),
            [],
        );
        const skillGridStore = useMemo(
            () =>
                createSelectionGridStore(
                    "AgentSkillLibrarySyncSkills",
                    "skills",
                ),
            [],
        );
        const previewGridStore = useMemo(() => {
            const gridStore = createSelectionGridStore(
                "AgentSkillLibrarySyncPreview",
                "changes",
            );
            gridStore.checkboxSelection = false;
            return gridStore;
        }, []);
        const resultGridStore = useMemo(() => {
            const gridStore = createSelectionGridStore(
                "AgentSkillLibrarySyncResults",
                "results",
            );
            gridStore.checkboxSelection = false;
            return gridStore;
        }, []);

        const selectedOrganizationIds = Array.from(
            organizationGridStore.selectedRowIds,
            String,
        );
        const selectedSkillIds = Array.from(
            skillGridStore.selectedRowIds,
            String,
        );
        const selectedSkills = store.librarySkills.filter((skill) =>
            selectedSkillIds.includes(skill.id),
        );
        const selectableSkills = store.librarySkills.filter((skill) =>
            Boolean(skill.skillVersionGroupId),
        );
        const filteredSkills = selectableSkills.filter(
            (skill) =>
                scopeFilter === "All" || skill.scopes.includes(scopeFilter),
        );
        const previewLoading = store.getTaskLoading(
            AgentSkillsStore.Tasks.PreviewLibrarySync,
        );
        const syncing = store.getTaskLoading(
            AgentSkillsStore.Tasks.SyncLibraryToTenants,
        );
        const organizationsLoading = store.getTaskLoading(
            AgentSkillsStore.Tasks.LoadSyncOrganizations,
        );

        const organizationColumns = useMemo<IColDef[]>(
            () => [
                {
                    field: "name",
                    headerName: "Organization",
                    flex: 1,
                    minWidth: 280,
                    display: "flex",
                },
            ],
            [],
        );
        const skillColumns = useMemo<IColDef[]>(
            () => [
                {
                    field: "name",
                    headerName: "Skill",
                    flex: 1,
                    minWidth: 200,
                    display: "flex",
                },
                {
                    field: "description",
                    headerName: "Description",
                    flex: 2,
                    minWidth: 260,
                    display: "flex",
                    renderCell: (
                        params: GridRenderCellParams<AgentSkillLibraryItem>,
                    ) => (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                        >
                            {params.row.description}
                        </Typography>
                    ),
                },
                {
                    field: "scopes",
                    headerName: "Scopes",
                    flex: 1,
                    minWidth: 180,
                    display: "flex",
                    valueGetter: (_, row: AgentSkillLibraryItem) =>
                        row.scopes
                            .map((scope) => scopeLabels[scope])
                            .join(", "),
                    renderCell: (
                        params: GridRenderCellParams<AgentSkillLibraryItem>,
                    ) => renderScopes(params.row.scopes),
                },
                {
                    field: "syncable",
                    headerName: "Sync status",
                    width: 170,
                    display: "flex",
                    valueGetter: (_, row: AgentSkillLibraryItem) =>
                        row.skillVersionGroupId ? "Ready" : "Resave to sync",
                    renderCell: (
                        params: GridRenderCellParams<AgentSkillLibraryItem>,
                    ) =>
                        params.row.skillVersionGroupId ? (
                            <Chip
                                label="Ready"
                                size="small"
                                color="success"
                                variant="outlined"
                            />
                        ) : (
                            <Chip
                                label="Resave to sync"
                                size="small"
                                color="warning"
                                variant="outlined"
                            />
                        ),
                },
            ],
            [],
        );
        const previewColumns = useMemo<IColDef[]>(
            () => [
                {
                    field: "organizationName",
                    headerName: "Organization",
                    flex: 1,
                    minWidth: 180,
                    display: "flex",
                },
                {
                    field: "name",
                    headerName: "Skill",
                    flex: 1,
                    minWidth: 180,
                    display: "flex",
                },
                {
                    field: "scopes",
                    headerName: "Scopes",
                    flex: 1,
                    minWidth: 160,
                    display: "flex",
                    valueGetter: (_, row: PreviewRow) =>
                        row.scopes
                            .map((scope) => scopeLabels[scope])
                            .join(", "),
                    renderCell: (params: GridRenderCellParams<PreviewRow>) =>
                        renderScopes(params.row.scopes),
                },
                {
                    field: "status",
                    headerName: "Tenant status",
                    width: 185,
                    display: "flex",
                    valueGetter: (_, row: PreviewRow) =>
                        previewStatusLabel[row.status],
                    renderCell: (params: GridRenderCellParams<PreviewRow>) => (
                        <Chip
                            label={previewStatusLabel[params.row.status]}
                            size="small"
                            color={
                                params.row.status === "NameConflict" ||
                                params.row.status ===
                                    "DuplicateActiveVersions" ||
                                params.row.status === "HistoricalIdConflict"
                                    ? "error"
                                    : params.row.status === "TenantOwned"
                                    ? "warning"
                                    : undefined
                            }
                            variant="outlined"
                        />
                    ),
                },
                {
                    field: "action",
                    headerName: "Planned action",
                    width: 190,
                    display: "flex",
                    sortable: false,
                    filterable: false,
                    renderCell: (params: GridRenderCellParams<PreviewRow>) => {
                        const row = params.row;
                        if (!isActionable(row.status)) {
                            return (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Skip
                                </Typography>
                            );
                        }

                        const label =
                            row.status === "Missing" ? "Create" : "Update";
                        return (
                            <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                    <Checkbox
                                        checked={
                                            selectedActions[row.id] ?? false
                                        }
                                        onChange={(event) =>
                                            setSelectedActions((actions) => ({
                                                ...actions,
                                                [row.id]: event.target.checked,
                                            }))
                                        }
                                    />
                                }
                                label={label}
                            />
                        );
                    },
                },
                {
                    field: "libraryModifiedOn",
                    headerName: "Library modified",
                    width: 145,
                    display: "flex",
                    renderCell: (params: GridRenderCellParams<PreviewRow>) =>
                        formatDate(params.row.libraryModifiedOn),
                },
                {
                    field: "tenantModifiedOn",
                    headerName: "Tenant modified",
                    width: 145,
                    display: "flex",
                    renderCell: (params: GridRenderCellParams<PreviewRow>) =>
                        formatDate(params.row.tenantModifiedOn),
                },
            ],
            [selectedActions],
        );
        const resultColumns = useMemo<IColDef[]>(
            () => [
                {
                    field: "organizationName",
                    headerName: "Organization",
                    flex: 1,
                    minWidth: 180,
                    display: "flex",
                },
                {
                    field: "skillName",
                    headerName: "Skill",
                    flex: 1,
                    minWidth: 180,
                    display: "flex",
                },
                {
                    field: "status",
                    headerName: "Result",
                    flex: 1,
                    minWidth: 180,
                    display: "flex",
                    valueGetter: (_, row: ResultRow) =>
                        syncResultLabels[row.status as AgentSkillSyncResultStatus]
                        ?? row.status,
                    renderCell: (params: GridRenderCellParams<ResultRow>) =>
                        syncResultLabels[
                            params.row.status as AgentSkillSyncResultStatus
                        ] ?? params.row.status,
                },
            ],
            [],
        );

        useEffect(() => {
            void organizationGridStore.setColumns(organizationColumns);
            void skillGridStore.setColumns(skillColumns);
            void previewGridStore.setColumns(previewColumns);
            void resultGridStore.setColumns(resultColumns);
        }, [
            organizationColumns,
            previewColumns,
            previewGridStore,
            resultColumns,
            resultGridStore,
            skillColumns,
            skillGridStore,
            organizationGridStore,
        ]);

        useEffect(() => {
            organizationGridStore.setRows(organizations);
            organizationGridStore.setLoading(organizationsLoading);
        }, [organizationGridStore, organizations, organizationsLoading]);

        useEffect(() => {
            skillGridStore.setRows(
                store.librarySkills.filter(
                    (skill) =>
                        scopeFilter === "All" ||
                        skill.scopes.includes(scopeFilter),
                ),
            );
            skillGridStore.setLoading(
                store.getTaskLoading(AgentSkillsStore.Tasks.LoadLibrary),
            );
        }, [scopeFilter, skillGridStore, store, store.librarySkills]);

        useEffect(() => {
            skillGridStore.isRowSelectable = (params: GridRowParams) =>
                Boolean(
                    (params.row as AgentSkillLibraryItem).skillVersionGroupId,
                );
            skillGridStore.getRowTooltip = (row: AgentSkillLibraryItem) =>
                row.skillVersionGroupId
                    ? undefined
                    : "This legacy skill must be resaved before it can be synchronized.";
        }, [skillGridStore]);

        useEffect(() => {
            if (!isOpen) return;

            setStep(0);
            setScopeFilter("All");
            setPreviewRows([]);
            setSelectedActions({});
            setSyncResult(undefined);
            setConfirmSync(false);
            organizationGridStore.removeAllSelectedRows();
            skillGridStore.removeAllSelectedRows();
            previewGridStore.removeAllSelectedRows();
            resultGridStore.removeAllSelectedRows();
            void store.loadSyncOrganizations().then((response) => {
                setOrganizations(
                    (response?.organizations ?? []).filter(
                        (organization) =>
                            organization.id !== store.currentOrganizationId,
                    ),
                );
            });
            void store.loadLibrary();
        }, [
            isOpen,
            organizationGridStore,
            previewGridStore,
            resultGridStore,
            skillGridStore,
            store,
        ]);

        const selectedActionCount = previewRows.filter(
            (row) => selectedActions[row.id],
        ).length;

        const selectAllOrganizations = () => {
            organizationGridStore.changeSelectedRowIds(
                organizations.map((organization) => organization.id),
            );
        };

        const selectFilteredSkills = () => {
            skillGridStore.changeSelectedRowIds([
                ...new Set([
                    ...selectedSkillIds,
                    ...filteredSkills.map((skill) => skill.id),
                ]),
            ]);
        };

        const preview = async () => {
            const skillVersionGroupIds = selectedSkills
                .map((skill) => skill.skillVersionGroupId)
                .filter((id): id is string => Boolean(id));
            const response = await store.previewLibrarySync({
                organizationIds: selectedOrganizationIds,
                skillVersionGroupIds,
            });
            if (!response) return;

            const scopesByGroupId = new Map(
                store.librarySkills
                    .filter((skill) => skill.skillVersionGroupId)
                    .map((skill) => [skill.skillVersionGroupId!, skill.scopes]),
            );
            const rows = (response.organizations ?? []).flatMap(
                (organization) =>
                    (organization.skills ?? []).map((skill) => ({
                        ...skill,
                        id: `${organization.organizationId}:${skill.skillVersionGroupId}`,
                        organizationId: organization.organizationId,
                        organizationName: organization.organizationName,
                        scopes:
                            scopesByGroupId.get(skill.skillVersionGroupId) ??
                            [],
                    })),
            );
            setPreviewRows(rows);
            setSelectedActions(
                Object.fromEntries(
                    rows.map((row) => [row.id, defaultAction(row.status)]),
                ),
            );
            previewGridStore.setRows(rows);
            setStep(2);
        };

        const sync = async () => {
            const response = await store.syncLibraryToTenants({
                organizationSyncRequests: selectedOrganizationIds.map(
                    (organizationId) => ({
                        organizationId,
                        syncRequests: previewRows
                            .filter(
                                (row) => row.organizationId === organizationId,
                            )
                            .map((row) => ({
                                skillVersionGroupId: row.skillVersionGroupId,
                                shouldCreate:
                                    row.status === "Missing" &&
                                    Boolean(selectedActions[row.id]),
                                shouldOverwrite:
                                    row.status === "DifferentVersion" &&
                                    Boolean(selectedActions[row.id]),
                            })),
                    }),
                ),
            });
            setConfirmSync(false);
            if (!response) return;

            const previewByKey = new Map(
                previewRows.map((row) => [
                    `${row.organizationId}:${row.skillVersionGroupId}`,
                    row,
                ]),
            );
            const resultRows: ResultRow[] = (
                response.organizations ?? []
            ).flatMap((organization) =>
                (organization.skills ?? []).map((skill) => {
                    const previewRow = previewByKey.get(
                        `${organization.organizationId}:${skill.skillVersionGroupId}`,
                    );
                    return {
                        id: `${organization.organizationId}:${skill.skillVersionGroupId}`,
                        organizationName:
                            previewRow?.organizationName ??
                            organization.organizationId,
                        skillName:
                            previewRow?.name ?? skill.skillVersionGroupId,
                        status: skill.status,
                    };
                }),
            );
            setSyncResult(response);
            resultGridStore.setRows(resultRows);
        };

        const close = () => {
            if (syncing) return;
            onClose();
        };

        const dialogTitle = syncResult
            ? "Library Sync Results"
            : "Sync Library Skills to Tenants";

        return (
            <>
                <Dialog open={isOpen} onClose={close} maxWidth="xl" fullWidth>
                    <DialogTitle>{dialogTitle}</DialogTitle>
                    <DialogContent dividers>
                        {!syncResult && (
                            <Stepper activeStep={step} sx={{ mb: 3 }}>
                                <Step>
                                    <StepLabel>Organizations</StepLabel>
                                </Step>
                                <Step>
                                    <StepLabel>Skills</StepLabel>
                                </Step>
                                <Step>
                                    <StepLabel>Preview &amp; Sync</StepLabel>
                                </Step>
                            </Stepper>
                        )}

                        {step === 0 && !syncResult && (
                            <Stack spacing={2}>
                                <Typography color="text.secondary">
                                    Select the customer organizations that
                                    should receive library skills.
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <AcxButton
                                        acxTheme="secondary"
                                        onClick={selectAllOrganizations}
                                        disabled={!organizations.length}
                                    >
                                        Select all customers
                                    </AcxButton>
                                    <AcxButton
                                        color="white"
                                        onClick={() =>
                                            organizationGridStore.removeAllSelectedRows()
                                        }
                                    >
                                        Clear selection
                                    </AcxButton>
                                </Stack>
                                <AcxDataGrid
                                    dataGridStore={organizationGridStore}
                                />
                            </Stack>
                        )}

                        {step === 1 && !syncResult && (
                            <Stack spacing={2}>
                                <Typography color="text.secondary">
                                    Filter the library by application scope,
                                    then select the skills to synchronize.
                                </Typography>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    useFlexGap
                                >
                                    {scopeOptions.map((scope) => (
                                        <AcxButton
                                            key={scope}
                                            color={
                                                scopeFilter === scope
                                                    ? "primary"
                                                    : "white"
                                            }
                                            onClick={() =>
                                                setScopeFilter(scope)
                                            }
                                        >
                                            {scope === "All"
                                                ? "All"
                                                : scopeLabels[scope]}
                                        </AcxButton>
                                    ))}
                                    <AcxButton
                                        acxTheme="secondary"
                                        onClick={selectFilteredSkills}
                                        disabled={!filteredSkills.length}
                                    >
                                        Select filtered
                                    </AcxButton>
                                    <AcxButton
                                        color="white"
                                        onClick={() =>
                                            skillGridStore.removeAllSelectedRows()
                                        }
                                    >
                                        Clear selection
                                    </AcxButton>
                                </Stack>
                                <AcxDataGrid dataGridStore={skillGridStore} />
                            </Stack>
                        )}

                        {step === 2 && !syncResult && (
                            <Stack spacing={2}>
                                <Typography color="text.secondary">
                                    Review the proposed changes. Missing skills
                                    are selected to create; out-of-date library
                                    skills are selected to update.
                                </Typography>
                                <AcxDataGrid dataGridStore={previewGridStore} />
                            </Stack>
                        )}

                        {syncResult && (
                            <Stack spacing={2}>
                                <Typography color="text.secondary">
                                    The synchronization completed. Review each
                                    tenant result below.
                                </Typography>
                                <AcxDataGrid dataGridStore={resultGridStore} />
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        {syncResult ? (
                            <AcxButton color="primary" onClick={close}>
                                Done
                            </AcxButton>
                        ) : (
                            <>
                                <AcxButton color="white" onClick={close}>
                                    Cancel
                                </AcxButton>
                                {step > 0 && (
                                    <AcxButton
                                        color="white"
                                        onClick={() =>
                                            setStep((current) => current - 1)
                                        }
                                        disabled={previewLoading}
                                    >
                                        Back
                                    </AcxButton>
                                )}
                                {step === 0 && (
                                    <AcxButton
                                        color="primary"
                                        onClick={() => setStep(1)}
                                        disabled={
                                            !selectedOrganizationIds.length ||
                                            organizationsLoading
                                        }
                                    >
                                        Next: Skills
                                    </AcxButton>
                                )}
                                {step === 1 && (
                                    <AcxButton
                                        color="primary"
                                        onClick={preview}
                                        loading={previewLoading}
                                        disabled={!selectedSkills.length}
                                    >
                                        Preview changes
                                    </AcxButton>
                                )}
                                {step === 2 && (
                                    <AcxButton
                                        color="primary"
                                        onClick={() => setConfirmSync(true)}
                                        disabled={!selectedActionCount}
                                    >
                                        Sync {selectedActionCount} change
                                        {selectedActionCount === 1 ? "" : "s"}
                                    </AcxButton>
                                )}
                            </>
                        )}
                    </DialogActions>
                </Dialog>
                <AcxConfirmationDialog
                    isOpen={confirmSync}
                    title="Sync library skills"
                    content={`${selectedActionCount} change${
                        selectedActionCount === 1 ? "" : "s"
                    } will be applied across the selected organizations.`}
                    confirmButtonText="Sync skills"
                    cancelButtonText="Cancel"
                    onClose={() => setConfirmSync(false)}
                    onConfirm={sync}
                    isLoading={syncing}
                    confirmButtonDisabled={!selectedActionCount}
                />
            </>
        );
    });

export default AgentSkillLibrarySyncDialog;
